(ns common.transaction-process.notifications
  "Pure notification rule matching and template rendering for transaction process.
   Rules are defined per template as :template/notifications.
   Dispatch/email sending lives in backend.domain.transaction-process-notifications."
  (:require
   [clojure.string :as string]))

(defn render-template
  "Replace {{variable}} placeholders in a template string with values from context map.
   Unknown variables are replaced with empty string."
  [template-str context]
  (if (string? template-str)
    (string/replace template-str
                    #"\{\{([a-zA-Z0-9_-]+)\}\}"
                    (fn [[_ k]]
                      (str (or (get context (keyword k)) ""))))
    ""))

(defn- stage-transition-matches?
  "Check if a stage-transition rule matches the given event."
  [rule {:keys [from-stage to-stage direction]}]
  (and (= :stage-transition (:notification-type rule))
       (or (nil? (:from-stage rule))
           (= (:from-stage rule) from-stage))
       (or (nil? (:to-stage rule))
           (= (:to-stage rule) to-stage))
       (or (nil? (:direction rule))
           (= (:direction rule) direction))))

(defn- creation-matches?
  [rule {:keys [notification-type]}]
  (and (= :creation (:notification-type rule))
       (= :creation notification-type)))

(defn match-rules
  "Given a notification event and a seq of notification rules from the template,
   return the rules that match the event.

   Event map keys:
     :notification-type  — :creation | :stage-transition
     :from-stage         — keyword (stage-transition only)
     :to-stage           — keyword (stage-transition only)
     :direction          — :forward | :backward (stage-transition only)"
  [rules event]
  (filterv (fn [rule]
             (case (:notification-type rule)
               :stage-transition (stage-transition-matches? rule event)
               :creation         (creation-matches? rule event)
               false))
           rules))

(defn resolve-recipient-teams
  "Given matched rules, return the union of all recipient team keywords."
  [matched-rules]
  (->> matched-rules
       (mapcat :recipients)
       set))

(defn resolve-team-emails
  "Given team-assignments map and a set of team keywords, return distinct emails
   for members of those teams. team-assignments is a map like:
   {:investment-team [{:value 123 :label \"Name\"} ...] ...}

   account-resolver-fn takes an account-id and returns the email or nil."
  [{:keys [team-assignments recipient-teams account-resolver-fn]}]
  (->> recipient-teams
       (mapcat #(get team-assignments %))
       (map (fn [m]
              (let [account-id (if (map? m) (:value m) m)]
                (when account-id
                  (account-resolver-fn account-id)))))
       (remove nil?)
       distinct
       vec))

(defn stage-label
  "Look up the human-readable label for a stage keyword from template stages."
  [template stage-kw]
  (or (->> (:template/stages template)
           (some #(when (= (:stage/id %) stage-kw) (:stage/label %))))
      (when stage-kw (name stage-kw))))

(defn build-context
  "Build the template variable context map from event data."
  [{:keys [process-name vehicle-name old-stage new-stage moved-by created-by tenant-name date]}]
  {:process-name  (or process-name "")
   :vehicle-name  (or vehicle-name "")
   :old-stage     (or old-stage "")
   :new-stage     (or new-stage "")
   :moved-by      (or moved-by "")
   :created-by    (or created-by "")
   :tenant-name   (or tenant-name "")
   :date          (or date "")})

(defn prepare-notifications
  "Given template notification rules and an event, return a seq of notification
   payloads ready for dispatch. Each payload has :subject, :body, :recipient-emails.

   Params:
     :rules              — seq of notification rule maps from template
     :event              — event map (:notification-type, :from-stage, :to-stage, :direction)
     :context-data       — map for build-context (:process-name, :vehicle-name, etc.)
     :team-assignments   — parsed team assignments map
     :account-resolver-fn — (fn [account-id] email-or-nil)"
  [{:keys [rules event context-data team-assignments account-resolver-fn]}]
  (let [matched (match-rules rules event)
        context (build-context context-data)]
    (->> matched
         (map (fn [rule]
                (let [recipient-teams (:recipients rule)
                      emails (resolve-team-emails
                              {:team-assignments    team-assignments
                               :recipient-teams     recipient-teams
                               :account-resolver-fn account-resolver-fn})]
                  (when (seq emails)
                    {:rule-id          (:rule-id rule)
                     :subject          (render-template (:subject rule) context)
                     :body             (render-template (:body rule) context)
                     :recipient-emails emails}))))
         (remove nil?)
         vec)))
